import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Link2, Link2Off, Search, Check, AlertCircle } from "lucide-react";

/**
 * Smart Link dropdown for connecting a Rabah Concept staff member to a
 * Jibble employee. Loads the Jibble People list once and presents a
 * searchable, lazily-rendered list. Reflects current linkage and
 * supports unlink.
 */
export default function JibbleLinkPicker({ staffId, currentJibbleUserId, onChanged }) {
  const [employees, setEmployees] = useState(null); // null = loading, [] = loaded
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let dropped = false;
    (async () => {
      try {
        const { data } = await api.get("/jibble/employees");
        if (!dropped) setEmployees(data.items || []);
      } catch (e) {
        if (!dropped)
          setError(e?.response?.data?.detail || "Failed to load Jibble employees");
      }
    })();
    return () => {
      dropped = true;
    };
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    if (!employees) return [];
    const q = query.trim().toLowerCase();
    if (!q) return employees.slice(0, 50);
    return employees
      .filter(
        (e) =>
          (e.full_name || "").toLowerCase().includes(q) ||
          (e.email || "").toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [employees, query]);

  const current =
    currentJibbleUserId && employees
      ? employees.find((e) => e.jibble_user_id === currentJibbleUserId)
      : null;

  const setLink = async (jibbleUserId) => {
    setSaving(true);
    try {
      await api.post(`/staff/${staffId}/jibble-link`, {
        jibble_user_id: jibbleUserId || null,
      });
      setOpen(false);
      onChanged?.(jibbleUserId || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save link");
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-rose-700"
        data-testid={`jibble-link-error-${staffId}`}
      >
        <AlertCircle className="h-3 w-3" /> {error}
      </span>
    );
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`jibble-link-trigger-${staffId}`}
        className={`inline-flex items-center gap-1.5 text-xs rounded-md px-2 py-1.5 border min-h-[32px] ${
          currentJibbleUserId
            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
            : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
        }`}
      >
        {currentJibbleUserId ? (
          <>
            <Link2 className="h-3.5 w-3.5" />
            <span className="font-medium truncate max-w-[140px]">
              {current?.full_name || "Linked"}
            </span>
          </>
        ) : (
          <>
            <Link2Off className="h-3.5 w-3.5" /> Link to Jibble
          </>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 z-30 w-72 bg-white border border-slate-200 rounded-md shadow-lg p-2"
          data-testid={`jibble-link-popover-${staffId}`}
        >
          <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Jibble employees…"
              className="flex-1 text-sm focus:outline-none"
              data-testid={`jibble-link-search-${staffId}`}
            />
          </div>
          {employees === null ? (
            <p className="text-xs text-slate-500 px-2 py-3">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-slate-500 px-2 py-3">No matches</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto py-1">
              {filtered.map((e) => {
                const linkedElsewhere =
                  e.linked_staff && e.linked_staff.id !== staffId;
                const isCurrent =
                  e.jibble_user_id === currentJibbleUserId;
                return (
                  <li key={e.jibble_user_id}>
                    <button
                      type="button"
                      disabled={saving || linkedElsewhere}
                      onClick={() => setLink(e.jibble_user_id)}
                      data-testid={`jibble-link-option-${e.jibble_user_id}`}
                      className={`w-full text-left px-2 py-1.5 rounded-md text-sm flex items-center justify-between gap-2 ${
                        linkedElsewhere
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-slate-50"
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block font-medium text-slate-900 truncate">
                          {e.full_name}
                        </span>
                        {(e.email || linkedElsewhere) && (
                          <span className="block text-[11px] text-slate-500 truncate">
                            {linkedElsewhere
                              ? `Already linked to ${e.linked_staff.full_name}`
                              : e.email}
                          </span>
                        )}
                      </span>
                      {isCurrent && (
                        <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {currentJibbleUserId && (
            <div className="border-t border-slate-100 pt-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => setLink(null)}
                data-testid={`jibble-link-unlink-${staffId}`}
                className="w-full text-left px-2 py-1.5 text-xs text-rose-700 hover:bg-rose-50 rounded-md"
              >
                Unlink from Jibble
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
